import { useCallback, useEffect, useState } from 'react';
import { fetchApi } from '../api';

/**
 * The one place the browser keeps the account's preferences.
 *
 * Before this, the settings screen wrote to `localStorage`, showed
 * "Настройки сохранены" whether or not anything happened, and none of the
 * notification switches changed any behaviour. Preferences now live on the server
 * (`GET`/`PATCH /api/settings`); this module loads them once, applies the ones
 * that affect presentation, and lets any component subscribe.
 *
 * The `localStorage` copy that remains is a *mirror*, used only to paint the
 * right theme on the first frame before the request comes back. The server is
 * always the source of truth.
 */

export type SettingsLanguage = 'ru' | 'en';
export type SettingsTheme = 'light' | 'dark' | 'system';

export interface UserSettings {
  language: SettingsLanguage;
  theme: SettingsTheme;
  notifyEmail: boolean;
  notifyPush: boolean;
  notifyLeads: boolean;
  notifyDeals: boolean;
  notifyStock: boolean;
  notifyBilling: boolean;
  updatedAt?: string | null;
}

export const DEFAULT_SETTINGS: UserSettings = {
  language: 'ru',
  theme: 'light',
  notifyEmail: true,
  notifyPush: true,
  notifyLeads: true,
  notifyDeals: true,
  notifyStock: true,
  notifyBilling: true,
  updatedAt: null,
};

const MIRROR_KEY = 'clientix_user_settings';
const DEVICE_KEY = 'clientix_device_prefs';

const LANGUAGES: SettingsLanguage[] = ['ru', 'en'];
const THEMES: SettingsTheme[] = ['light', 'dark', 'system'];

function coerce(raw: unknown): UserSettings {
  const input = (raw || {}) as Record<string, unknown>;
  const bool = (key: keyof UserSettings) =>
    typeof input[key] === 'boolean' ? (input[key] as boolean) : (DEFAULT_SETTINGS[key] as boolean);
  return {
    language: LANGUAGES.includes(input.language as SettingsLanguage)
      ? (input.language as SettingsLanguage)
      : DEFAULT_SETTINGS.language,
    theme: THEMES.includes(input.theme as SettingsTheme)
      ? (input.theme as SettingsTheme)
      : DEFAULT_SETTINGS.theme,
    notifyEmail: bool('notifyEmail'),
    notifyPush: bool('notifyPush'),
    notifyLeads: bool('notifyLeads'),
    notifyDeals: bool('notifyDeals'),
    notifyStock: bool('notifyStock'),
    notifyBilling: bool('notifyBilling'),
    updatedAt: typeof input.updatedAt === 'string' ? input.updatedAt : null,
  };
}

function readMirror(): UserSettings {
  try {
    const stored = localStorage.getItem(MIRROR_KEY);
    return stored ? coerce(JSON.parse(stored)) : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function writeMirror(settings: UserSettings): void {
  try {
    localStorage.setItem(MIRROR_KEY, JSON.stringify(settings));
  } catch {
    /* private mode, quota — presentation still works for this session */
  }
}

let current: UserSettings = typeof localStorage === 'undefined' ? DEFAULT_SETTINGS : readMirror();
let loadedFromServer = false;
let inflight: Promise<UserSettings> | null = null;
const listeners = new Set<(settings: UserSettings) => void>();

function darkMediaQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  return window.matchMedia('(prefers-color-scheme: dark)');
}

export function resolveTheme(theme: SettingsTheme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return darkMediaQuery()?.matches ? 'dark' : 'light';
}

/** Everything about a preference that is visible without a re-render. */
function applyPresentation(settings: UserSettings): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const resolved = resolveTheme(settings.theme);
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  root.lang = settings.language;
}

function emit(): void {
  for (const listener of listeners) listener(current);
}

function setCurrent(next: UserSettings): void {
  current = next;
  writeMirror(next);
  applyPresentation(next);
  emit();
}

export function getUserSettings(): UserSettings {
  return current;
}

export function subscribeUserSettings(listener: (settings: UserSettings) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Load once per session. Rejects on failure so callers can show a real error. */
export function loadUserSettings(force = false): Promise<UserSettings> {
  if (!force && loadedFromServer) return Promise.resolve(current);
  if (!inflight) {
    inflight = fetchApi('/settings')
      .then((data) => {
        loadedFromServer = true;
        setCurrent(coerce(data));
        return current;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** PATCH the preferences. Throws the server's message when it fails. */
export async function saveUserSettings(patch: Partial<UserSettings>): Promise<UserSettings> {
  const data = await fetchApi('/settings', { method: 'PATCH', body: JSON.stringify(patch) });
  loadedFromServer = true;
  setCurrent(coerce(data));
  return current;
}

// Follow the OS while the account is on "как в системе".
const media = darkMediaQuery();
media?.addEventListener?.('change', () => {
  if (current.theme === 'system') applyPresentation(current);
});
applyPresentation(current);

// ── device-local preferences ────────────────────────────────────────────────
// These two genuinely cannot be anything but per-device, and `UserSettings` has
// no column for them. The settings screen says so out loud instead of implying
// they follow the account.

export interface DevicePrefs {
  compactMode: boolean;
  sound: boolean;
}

const DEFAULT_DEVICE_PREFS: DevicePrefs = { compactMode: false, sound: true };
const deviceListeners = new Set<(prefs: DevicePrefs) => void>();

let devicePrefs: DevicePrefs = (() => {
  try {
    const stored = JSON.parse(localStorage.getItem(DEVICE_KEY) || '{}');
    return {
      compactMode: typeof stored.compactMode === 'boolean' ? stored.compactMode : DEFAULT_DEVICE_PREFS.compactMode,
      sound: typeof stored.sound === 'boolean' ? stored.sound : DEFAULT_DEVICE_PREFS.sound,
    };
  } catch {
    return DEFAULT_DEVICE_PREFS;
  }
})();

function applyDevicePrefs(prefs: DevicePrefs): void {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle('compact-ui', prefs.compactMode);
}

export function getDevicePrefs(): DevicePrefs {
  return devicePrefs;
}

export function setDevicePrefs(patch: Partial<DevicePrefs>): DevicePrefs {
  devicePrefs = { ...devicePrefs, ...patch };
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(devicePrefs));
  } catch {
    /* ignore */
  }
  applyDevicePrefs(devicePrefs);
  for (const listener of deviceListeners) listener(devicePrefs);
  return devicePrefs;
}

export function subscribeDevicePrefs(listener: (prefs: DevicePrefs) => void): () => void {
  deviceListeners.add(listener);
  return () => deviceListeners.delete(listener);
}

applyDevicePrefs(devicePrefs);

// ── formatting ──────────────────────────────────────────────────────────────
// The language switch drives every date and number the interface renders, which
// is what it can honestly promise: the interface copy itself is Russian only, and
// the settings screen states that rather than offering a translation that does
// not exist.

export function activeLocale(): string {
  return current.language === 'en' ? 'en-US' : 'ru-RU';
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(activeLocale(), options).format(value);
}

export function formatMoney(value: number): string {
  return `${formatNumber(Math.round(value))} ₽`;
}

export function formatDate(value: string | number | Date, options: Intl.DateTimeFormatOptions): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(activeLocale(), options).format(date);
}

// ── hooks ───────────────────────────────────────────────────────────────────

export type SettingsStatus = 'loading' | 'ready' | 'error';

export function useUserSettings() {
  const [settings, setSettings] = useState<UserSettings>(getUserSettings);
  const [status, setStatus] = useState<SettingsStatus>(loadedFromServer ? 'ready' : 'loading');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((force = false) => {
    setStatus((previous) => (previous === 'ready' ? previous : 'loading'));
    return loadUserSettings(force)
      .then(() => {
        setStatus('ready');
        setError(null);
      })
      .catch((err: unknown) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Не удалось загрузить настройки');
      });
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeUserSettings(setSettings);
    void load();
    return unsubscribe;
  }, [load]);

  return { settings, status, error, reload: () => load(true) };
}

export function useDevicePrefs() {
  const [prefs, setPrefs] = useState<DevicePrefs>(getDevicePrefs);
  useEffect(() => subscribeDevicePrefs(setPrefs), []);
  return prefs;
}
